import { z } from 'zod';

import { ClientActionAgentSchema } from './agent-activity';
import type { WorkspaceOperationPending } from './workspace-operation';
import type {
  UiObjectRef,
  UiScalar,
  WorkspaceNavigationArgsSchema,
  WorkspaceNavigationDescriptor,
  WorkspaceViewRevision,
  WorkspaceViewScope,
  WorkspaceViewSnapshot,
} from './workspace-view';
import {
  isWithinWorkspaceViewCodePointLimit,
  workspaceNavigationArgsSchema,
} from './workspace-view';
import type { WorkspaceViewReadPending } from './workspace-view-read';

export type NavigationArgs = Readonly<
  Record<string, UiScalar | readonly UiScalar[]>
>;
export interface WorkspaceNavigationRequest extends WorkspaceViewScope {
  requestId: string;
  revision: WorkspaceViewRevision;
  actionId: string;
  args: NavigationArgs;
}
export interface WorkspaceNavigationTarget extends WorkspaceViewScope {
  revision: WorkspaceViewRevision;
  surfaceId: string;
  route: string;
  title: string;
  focusRef?: UiObjectRef;
  dialog?: { id: string; tab: string; anchor?: string };
  sidebar?: { side: 'left' | 'right'; entryId: string; sectionId?: string };
}
export type WorkspaceNavigationOutcome =
  | {
      status: 'reached';
      target: WorkspaceNavigationTarget;
      context: WorkspaceViewSnapshot;
      results?: readonly WorkspaceNavigationDescriptor[];
    }
  | { status: 'blocked' | 'stale' | 'offline' | 'timeout'; messageKey: string };

/** Pending navigation travels through the existing Copilot client-action stream. */
export interface WorkspaceNavigationPending {
  kind: 'workspace-navigation';
  status: 'pending';
  endpoint: string;
  timeoutMs: number;
  clientInstanceId: string;
  request: WorkspaceNavigationRequest;
}
export interface WorkspaceNavigationAck {
  kind: 'workspace-navigation';
  clientInstanceId: string;
  requestId: string;
  workspaceId: string;
  sourceViewId: string;
  sourceRevision: WorkspaceViewRevision;
  outcome: WorkspaceNavigationOutcome;
}

export const DocumentClientActionPendingSchema = z.object({
  kind: z.literal('document'),
  status: z.literal('pending'),
  endpoint: z.string().min(1),
  timeoutMs: z.number().positive(),
  workspaceId: z.string().min(1),
  targetDocId: z.string().min(1),
  clientInstanceId: z.string().min(1),
  agent: ClientActionAgentSchema,
});
export type DocumentClientActionPending = z.infer<
  typeof DocumentClientActionPendingSchema
>;
export type CopilotClientActionPending =
  | DocumentClientActionPending
  | WorkspaceNavigationPending
  | WorkspaceOperationPending
  | WorkspaceViewReadPending;

/** Validate actual values against the owner's existing bounded argument schema. */
export function validateNavigationArgs(
  schema: WorkspaceNavigationArgsSchema,
  input: unknown
): NavigationArgs | null {
  try {
    const blueprint = workspaceNavigationArgsSchema.safeParse(schema);
    if (!blueprint.success) return null;
    schema = blueprint.data;
    const fields: Record<string, z.ZodTypeAny> = Object.create(null);
    for (const [key, property] of Object.entries(schema.properties)) {
      let field: z.ZodTypeAny;
      switch (property.type) {
        case 'string': {
          const string = z
            .string()
            .max(property.maxLength * 2)
            .refine(value =>
              isWithinWorkspaceViewCodePointLimit(value, property.maxLength)
            );
          field = property.enum
            ? string.refine(value => property.enum!.includes(value))
            : string;
          break;
        }
        case 'number': {
          const number = z
            .number()
            .finite()
            .min(property.minimum)
            .max(property.maximum);
          field = property.integer ? number.int() : number;
          break;
        }
        case 'boolean':
          field = z.boolean();
          break;
        case 'null':
          field = z.null();
          break;
        case 'array':
          field = z
            .array(
              property.items === 'string'
                ? z.string()
                : property.items === 'number'
                  ? z.number().finite()
                  : z.boolean()
            )
            .max(property.maxItems);
          break;
      }
      fields[key] = schema.required.includes(key) ? field : field.optional();
    }
    if (
      schema.required.some(
        key => !Object.prototype.hasOwnProperty.call(fields, key)
      )
    )
      return null;
    const result = z.object(fields).strict().safeParse(input);
    if (
      !result.success ||
      Object.values(result.data).some(value => value === undefined)
    )
      return null;
    return Object.freeze(
      Object.fromEntries(
        Object.entries(result.data).map(([key, value]) => [
          key,
          Array.isArray(value) ? Object.freeze(value) : value,
        ])
      )
    ) as NavigationArgs;
  } catch {
    return null;
  }
}
